import React, { useState, useEffect } from 'react';
import { Table, Form, Input, Button, Space, Select, DatePicker, Tag, Modal, Drawer, Descriptions, Timeline, Divider, message } from 'antd';
import { SearchOutlined, ReloadOutlined, PlusOutlined, ExportOutlined, ImportOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { getSerialList, deleteSerial, rebindSerial } from '../../api/serial';
import { getPlatformList } from '../../api/config';
import { useVisibleFields, hasPerm } from '../../hooks/usePermission';
import { getFieldMap } from '../../constants/fieldResource';
import { downloadCsv } from '../../utils/exportCsv';

const { RangePicker } = DatePicker;

const PLATFORM_OPTIONS = ['电信', '移动', '联通', '涂鸦'].map((v) => ({ value: v, label: v }));
const FACTORY_OPTIONS = ['达天', '凯木金', '中嘉'].map((v) => ({ value: v, label: v }));
const MODEL_OPTIONS = ['YLF-X1', 'YLF-G2', 'YLF-C3', 'YLF-T4'].map((v) => ({ value: v, label: v }));
const TIME_TYPE_OPTIONS = [
    { value: 'issue_time', label: '下发时间' },
    { value: 'bind_time', label: '绑定时间' },
    { value: 'ship_time', label: '发货时间' },
    { value: 'return_time', label: '退货时间' },
];
const BOUND_STATUSES = ['已绑定', '已发货', '已退货'];
const RESETABLE_STATUSES = ['已授权(下发)', '已绑定', '已退货'];
const UNBINDABLE_STATUSES = ['已绑定', '已退货'];

const AUTH_STATUS_OPTIONS = ['未授权', '已授权(下发)'].map((v) => ({ value: v, label: v }));
const BIND_STATUS_OPTIONS = ['未绑定', '已绑定'].map((v) => ({ value: v, label: v }));
const LOGISTICS_STATUS_OPTIONS = ['未发货', '已发货', '已退货'].map((v) => ({ value: v, label: v }));

const AUTH_STATUS_COLOR = { '未授权': 'default', '已授权(下发)': 'processing' };
const BIND_STATUS_COLOR = { '未绑定': 'default', '已绑定': 'success' };
const LOGISTICS_STATUS_COLOR = { '未发货': 'default', '已发货': 'cyan', '已退货': 'orange' };

const getAuthStatus = (s) => (s === '未使用' ? '未授权' : '已授权(下发)');
const getBindStatus = (s) => (BOUND_STATUSES.includes(s) ? '已绑定' : '未绑定');
const getLogisticsStatus = (s) => (s === '已发货' ? '已发货' : s === '已退货' ? '已退货' : '未发货');

const SerialList = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const watchPlatform = Form.useWatch('platform', form);
    const watchQueryField = Form.useWatch('query_field', form);
    const [platformFieldsMap, setPlatformFieldsMap] = useState({});
    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailSerial, setDetailSerial] = useState(null);

    const fetchList = async (params = {}) => {
        setLoading(true);
        try {
            const res = await getSerialList({
                page: params.page || 1,
                page_size: params.page_size || 10,
                order_no: params.order_no,
                mac: params.mac,
                sn: params.sn,
                platform: params.platform,
                query_field: params.query_field,
                query_value: params.query_value,
                key_value: params.key_value,
                factory: params.factory,
                model: params.model,
                auth_status: params.auth_status,
                bind_status: params.bind_status,
                logistics_status: params.logistics_status,
                time_type: params.time_type,
                date_range: params.date_range,
            });
            if (res.code === 0) {
                setDataSource(res.data.list);
                setPagination((prev) => ({ ...prev, total: res.data.total }));
            } else {
                message.error(res.message || '获取列表失败');
            }
        } catch (error) {
            console.error('Fetch serial list failed:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const orderNo = searchParams.get('order_no');
        const timeType = searchParams.get('time_type');
        const date = searchParams.get('date');
        if (timeType && date) {
            form.setFieldsValue({ time_type: timeType, date_range: [dayjs(date), dayjs(date)] });
            fetchList({ time_type: timeType, date_range: [date, date], page: 1 });
        } else if (orderNo) {
            form.setFieldsValue({ order_no: orderNo });
            fetchList({ order_no: orderNo, page: 1 });
        } else {
            fetchList();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const fetchPlatformFields = async () => {
            const res = await getPlatformList();
            if (res.code === 0) {
                const map = {};
                res.data.list.forEach((p) => {
                    const fields = p.fields || [];
                    map[p.name] = {
                        names: fields.filter((f) => !f.is_key).map((f) => f.name),
                        keyField: (fields.find((f) => f.is_key) || {}).name,
                    };
                });
                setPlatformFieldsMap(map);
            }
        };
        fetchPlatformFields();
    }, []);

    const handleSearch = () => {
        const values = form.getFieldsValue();
        const params = { ...values };
        if (params.date_range && params.date_range[0]) {
            params.date_range = [params.date_range[0].format('YYYY-MM-DD'), params.date_range[1].format('YYYY-MM-DD')];
        }
        fetchList({ ...params, page: 1 });
    };

    const handleReset = () => {
        form.resetFields();
        fetchList({ page: 1 });
    };

    const handleDetail = (record) => {
        setDetailSerial(record);
        setDetailOpen(true);
    };

    const handleExport = () => {
        const fields = getFieldMap()['serial:list'] || [];
        const columns = fields.filter((f) => visibleFields.has(f.code)).map((f) => {
            if (f.code === 'key') {
                return { label: f.label, value: (r) => { const kf = platformFieldsMap[r.platform]?.keyField; return r.fields?.[kf] || ''; } };
            }
            if (f.code === 'fields') {
                return { label: f.label, value: (r) => Object.entries(r.fields || {}).map(([k, v]) => `${k}:${v}`).join('; ') };
            }
            return { label: f.label, value: (r) => r[f.code] ?? '' };
        });
        downloadCsv('串码池.csv', columns, dataSource);
    };

    const doVoid = async (record) => {
        const res = await deleteSerial({ id: record.id });
        if (res.code === 0) {
            message.success('作废成功');
            fetchList();
        } else {
            message.error(res.message || '作废失败');
        }
    };

    const handleVoid = (record) => {
        Modal.confirm({
            title: '作废确认',
            content: `确定作废串码 ${record.sn} 吗？`,
            okText: '确认',
            okButtonProps: { danger: true },
            cancelText: '取消',
            centered: true,
            onOk: () => doVoid(record),
        });
    };

    const doUnbind = async (record) => {
        const res = await rebindSerial({ id: record.id, action: 'unbind' });
        if (res.code === 0) {
            message.success(`串码 ${record.sn} 已解绑 CPUID`);
            fetchList();
        } else {
            message.error(res.message || '操作失败');
        }
    };

    const handleUnbind = (record) => {
        Modal.confirm({
            title: '解绑确认',
            content: `确定解绑串码 ${record.sn} 已绑定的 CPUID（${record.cpu_id}）吗？`,
            okText: '确认解绑',
            okButtonProps: { danger: true },
            cancelText: '取消',
            centered: true,
            onOk: () => doUnbind(record),
        });
    };

    const doResetAuth = async (record) => {
        const res = await rebindSerial({ id: record.id, action: 'reset' });
        if (res.code === 0) {
            message.success(`串码 ${record.sn} 授权状态已重置`);
            fetchList();
        } else {
            message.error(res.message || '操作失败');
        }
    };

    const handleResetAuth = (record) => {
        Modal.confirm({
            title: '重置确认',
            content: `确定重置串码 ${record.sn} 的授权状态吗？`,
            okText: '确认重置',
            okButtonProps: { danger: true },
            cancelText: '取消',
            centered: true,
            onOk: () => doResetAuth(record),
        });
    };

    const fieldInfo = watchPlatform ? platformFieldsMap[watchPlatform] : null;
    const queryFieldOptions = fieldInfo
        ? [...fieldInfo.names, ...(fieldInfo.keyField ? [fieldInfo.keyField] : [])].map((v) => ({ value: v, label: v }))
        : [];
    const visibleFields = useVisibleFields('serial:list');
    const show = (code) => visibleFields.has(code);
    const dynamicFieldColumns = [{
        title: '平台字段', dataIndex: 'fields', key: 'platform_fields', width: 220,
        render: (fields, record) => {
            const keyField = platformFieldsMap[record.platform]?.keyField;
            const entries = Object.entries(fields || {}).filter(([k]) => k !== keyField);
            return (
                <Space wrap size={4}>
                    {entries.map(([k, v]) => (
                        <Tag key={k}>{k}：{v}</Tag>
                    ))}
                </Space>
            );
        },
    }];

    const columns = [
        show('mac') && { title: 'MAC', dataIndex: 'mac', key: 'mac', width: 180, fixed: 'left', render: (v) => <div style={{ whiteSpace: 'pre-wrap' }}>{v || '-'}</div> },
        show('platform') && { title: '运营平台', dataIndex: 'platform', key: 'platform', width: 90 },
        ...(show('fields') ? dynamicFieldColumns : []),
        show('sn') && { title: 'SN', dataIndex: 'sn', key: 'sn', width: 140 },
        show('key') && {
            title: '密钥', key: 'key_field', width: 140,
            render: (_, record) => {
                const keyField = platformFieldsMap[record.platform]?.keyField;
                return keyField && record.fields?.[keyField] ? record.fields[keyField] : '-';
            },
        },
        show('order_no') && { title: '工单号', dataIndex: 'order_no', key: 'order_no', width: 130, render: (v) => v || '-' },
        show('factory') && { title: '生产工厂', dataIndex: 'factory', key: 'factory', width: 100 },
        show('model') && { title: '设备型号', dataIndex: 'model', key: 'model', width: 110 },
        show('auth_status') && {
            title: '授权状态', dataIndex: 'status', key: 'auth_status', width: 130,
            render: (v) => { const t = getAuthStatus(v); return <Tag color={AUTH_STATUS_COLOR[t] || 'default'}>{t}</Tag>; },
        },
        show('bind_status') && {
            title: '绑定状态', dataIndex: 'status', key: 'bind_status', width: 110,
            render: (v) => { const t = getBindStatus(v); return <Tag color={BIND_STATUS_COLOR[t] || 'default'}>{t}</Tag>; },
        },
        show('logistics_status') && {
            title: '物流状态', dataIndex: 'status', key: 'logistics_status', width: 110,
            render: (v) => { const t = getLogisticsStatus(v); return <Tag color={LOGISTICS_STATUS_COLOR[t] || 'default'}>{t}</Tag>; },
        },
        show('issue_time') && { title: '下发时间', dataIndex: 'issue_time', key: 'issue_time', width: 170, render: (v) => v || '-' },
        show('cpu_id') && { title: 'CPUID', dataIndex: 'cpu_id', key: 'cpu_id', width: 120, render: (v) => v || '-' },
        show('bind_time') && { title: 'CPUID绑定时间', dataIndex: 'bind_time', key: 'bind_time', width: 170, render: (v) => v || '-' },
        show('import_time') && { title: '导入时间', dataIndex: 'import_time', key: 'import_time', width: 160 },
        {
            title: '操作', key: 'action', width: 180, fixed: 'right',
            onHeaderCell: () => ({ style: { textAlign: 'center' } }),
            render: (_, record) => (
                <Space>
                    {hasPerm('serial:detail') && (
                        <Button type="link" onClick={() => handleDetail(record)}>详情</Button>
                    )}
                    {hasPerm('serial:unbind') && UNBINDABLE_STATUSES.includes(record.status) && (
                        <Button type="link" danger onClick={() => handleUnbind(record)}>解绑CPUID</Button>
                    )}
                    {hasPerm('serial:reset') && RESETABLE_STATUSES.includes(record.status) && (
                        <Button type="link" danger onClick={() => handleResetAuth(record)}>重置授权</Button>
                    )}
                    {hasPerm('serial:void') && record.status === '未使用' && (
                        <Button type="link" danger onClick={() => handleVoid(record)}>作废</Button>
                    )}
                </Space>
            ),
        },
    ].filter(Boolean);

    return (
        <div style={{ padding: '16px' }}>
            <Form form={form} layout="inline" initialValues={{ time_type: 'issue_time' }} style={{ marginBottom: 16, rowGap: 16 }}>
                <Form.Item name="mac">
                    <Input placeholder="MAC" allowClear style={{ width: 170 }} />
                </Form.Item>
                <Form.Item name="sn">
                    <Input placeholder="SN" allowClear style={{ width: 150 }} />
                </Form.Item>
                <Form.Item name="order_no">
                    <Input placeholder="工单号" allowClear style={{ width: 150 }} />
                </Form.Item>
                <Form.Item name="platform">
                    <Select placeholder="运营平台" allowClear options={PLATFORM_OPTIONS} style={{ width: 120 }} />
                </Form.Item>
                <Form.Item name="query_field">
                    <Select placeholder="查询字段" allowClear disabled={!watchPlatform} options={queryFieldOptions} style={{ width: 120 }} />
                </Form.Item>
                <Form.Item name="query_value">
                    <Input placeholder="字段值" allowClear disabled={!watchQueryField} style={{ width: 160 }} />
                </Form.Item>
                <Form.Item name="key_value">
                    <Input placeholder="密钥" allowClear style={{ width: 140 }} />
                </Form.Item>
                <Form.Item name="factory">
                    <Select placeholder="生产工厂" allowClear options={FACTORY_OPTIONS} style={{ width: 120 }} />
                </Form.Item>
                <Form.Item name="model">
                    <Select placeholder="设备型号" allowClear options={MODEL_OPTIONS} style={{ width: 130 }} />
                </Form.Item>
                <Form.Item name="auth_status">
                    <Select placeholder="授权状态" allowClear options={AUTH_STATUS_OPTIONS} style={{ width: 130 }} />
                </Form.Item>
                <Form.Item name="bind_status">
                    <Select placeholder="绑定状态" allowClear options={BIND_STATUS_OPTIONS} style={{ width: 120 }} />
                </Form.Item>
                <Form.Item name="logistics_status">
                    <Select placeholder="物流状态" allowClear options={LOGISTICS_STATUS_OPTIONS} style={{ width: 120 }} />
                </Form.Item>
                <Form.Item>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Divider type="vertical" style={{ height: 32, margin: 0 }} />
                        <Space.Compact>
                            <Form.Item name="time_type" noStyle>
                                <Select options={TIME_TYPE_OPTIONS} style={{ width: 110 }} />
                            </Form.Item>
                            <Form.Item name="date_range" noStyle>
                                <RangePicker placeholder={['开始日期', '结束日期']} style={{ width: 240 }} />
                            </Form.Item>
                        </Space.Compact>
                    </div>
                </Form.Item>
                <Form.Item>
                    <Space>
                        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
                        <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
                    </Space>
                </Form.Item>
            </Form>

            <div style={{ marginBottom: 16 }}>
                <Space>
                    {hasPerm('serial:import') && (
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/serial/import')}>导入串码</Button>
                    )}
                    {hasPerm('serial:import-old') && (
                        <Button color="orange" variant="solid" icon={<ImportOutlined />} onClick={() => navigate('/serial/import-old')}>导入旧平台串码</Button>
                    )}
                    {hasPerm('serial:export') && (
                        <Button icon={<ExportOutlined />} onClick={handleExport}>导出</Button>
                    )}
                </Space>
            </div>

            <Table
                rowKey="id"
                columns={columns}
                dataSource={dataSource}
                loading={loading}
                pagination={pagination}
                scroll={{ x: 2000 }}
            />

            <Drawer
                title={`串码详情 - ${detailSerial?.sn || ''}`}
                width={640}
                open={detailOpen}
                onClose={() => setDetailOpen(false)}
            >
                {detailSerial && (() => {
                    const keyField = platformFieldsMap[detailSerial.platform]?.keyField;
                    const entries = Object.entries(detailSerial.fields || {}).filter(([k]) => k !== keyField);
                    return (
                        <>
                            <Descriptions bordered size="small" column={2}>
                                {show('sn') && <Descriptions.Item label="SN">{detailSerial.sn}</Descriptions.Item>}
                                {show('mac') && <Descriptions.Item label="MAC"><div style={{ whiteSpace: 'pre-wrap' }}>{detailSerial.mac}</div></Descriptions.Item>}
                                {show('platform') && <Descriptions.Item label="运营平台">{detailSerial.platform}</Descriptions.Item>}
                                {show('auth_status') && (
                                    <Descriptions.Item label="授权状态">
                                        <Tag color={AUTH_STATUS_COLOR[getAuthStatus(detailSerial.status)] || 'default'}>{getAuthStatus(detailSerial.status)}</Tag>
                                    </Descriptions.Item>
                                )}
                                {show('bind_status') && (
                                    <Descriptions.Item label="绑定状态">
                                        <Tag color={BIND_STATUS_COLOR[getBindStatus(detailSerial.status)] || 'default'}>{getBindStatus(detailSerial.status)}</Tag>
                                    </Descriptions.Item>
                                )}
                                {show('logistics_status') && (
                                    <Descriptions.Item label="物流状态">
                                        <Tag color={LOGISTICS_STATUS_COLOR[getLogisticsStatus(detailSerial.status)] || 'default'}>{getLogisticsStatus(detailSerial.status)}</Tag>
                                    </Descriptions.Item>
                                )}
                                {show('order_no') && <Descriptions.Item label="工单号">{detailSerial.order_no || '-'}</Descriptions.Item>}
                                <Descriptions.Item label="设备型号">{detailSerial.model}</Descriptions.Item>
                                <Descriptions.Item label="生产工厂">{detailSerial.factory}</Descriptions.Item>
                                {show('cpu_id') && <Descriptions.Item label="CPUID">{detailSerial.cpu_id || '-'}</Descriptions.Item>}
                                {show('key') && <Descriptions.Item label="密钥">{keyField && detailSerial.fields?.[keyField] ? detailSerial.fields[keyField] : '-'}</Descriptions.Item>}
                                {show('import_time') && <Descriptions.Item label="导入时间">{detailSerial.import_time}</Descriptions.Item>}
                                {show('fields') && (
                                    <Descriptions.Item label="平台字段" span={2}>
                                        <Space wrap size={4}>
                                            {entries.map(([k, v]) => (
                                                <Tag key={k}>{k}：{v}</Tag>
                                            ))}
                                        </Space>
                                    </Descriptions.Item>
                                )}
                            </Descriptions>

                            <div style={{ margin: '16px 0 8px', fontWeight: 500 }}>状态变更记录</div>
                            <Timeline
                                items={(detailSerial.history || []).map((h) => ({
                                    color: h.action === '退货' ? 'red' : h.action === '发货' ? 'green' : 'blue',
                                    children: (
                                        <div>
                                            <Space size={8}>
                                                <span>{h.action}</span>
                                                <span style={{ color: '#999' }}>{h.time}</span>
                                            </Space>
                                            {h.cpu_id && <div style={{ color: '#999' }}>CPUID：{h.cpu_id}</div>}
                                        </div>
                                    ),
                                }))}
                            />
                        </>
                    );
                })()}
            </Drawer>
        </div>
    );
};

export default SerialList;
